<script>
  import { onMount } from 'svelte'
  import { loadLoginPageData } from '../../services/auth'
  import { Input, Label } from 'sveltestrap' // Keeping only necessary Sveltestrap components
  import LanguageDropdown from '../../common/LanguageDropdown.svelte'
  import { fade } from 'svelte/transition'
  import { DEFAULT_LOCALE } from '../../env/config'
  import { setupI18n } from '../../services/i18n'
  import { navigate } from 'svelte-routing' // Import navigate for navigation
  import { callUser } from '../../services/user' // Import callUser

  // Default data in case API call fails or is slow
  const defaultData = {
    title: 'Iniciar Sesión',
    userLabel: 'Usuario',
    passwordLabel: 'Contraseña',
    emailPlaceholder: 'Ingreso usuario',
    passwordPlaceholder: 'Ingreso contraseña',
    rememberLabel: 'Recuérdame',
    singInButtonLabel: 'Ingresar',
    forgetPassLabel: '¿Olvidaste tu contraseña?',
    copyright: 'Rastros. Todos los derechos reservados.', // Default copyright
    logo: { url: '' }, // Set to empty string as it should load from API
    // Default error messages (will be overwritten by API if available)
    emailEmptyError: 'Por favor, ingresa un email.',
    passwordEmptyError: 'Por favor, ingresa una contraseña.',
  }

  let data = { ...defaultData }
  let email = ''
  let password = ''
  let errorMessage = ''
  let isLoading = false
  let showPassword = false
  let rememberMe = false
  let availableLanguages = [] // New variable for dynamic languages
  let currentYear = new Date().getFullYear()

  // Load page data from API
  async function loadPageData() {
    try {
      isLoading = true
      const response = await loadLoginPageData() // This calls the auth service function
      if (response) {
        // Merge API data with default data
        data = { ...defaultData, ...response }
        console.log('Datos cargados (después de merge):', data)

        // Populate availableLanguages from API response
        availableLanguages = []
        if (data.locale) {
          availableLanguages.push({
            label: data.locale,
            value: data.locale,
            flag: `/assets/images/flags/${data.locale.split('-')[0]}.jpg`,
          })
        }
        if (response.localizations && Array.isArray(response.localizations)) {
          response.localizations.forEach((loc) => {
            availableLanguages.push({
              label: loc.locale,
              value: loc.locale,
              flag: `/assets/images/flags/${loc.locale.split('-')[0]}.jpg`,
            })
          })
        }
        // Sort languages by label for consistent display
        availableLanguages.sort((a, b) => a.label.localeCompare(b.label))
      }
    } catch (error) {
      console.error('Error cargando datos:', error)
      errorMessage =
        'Error al cargar la configuración. Por favor, recarga la página.'
    } finally {
      isLoading = false
    }
  }

  // Handle language change
  function handleLanguageChange() {
    console.log('handleLanguageChange called. Reloading page data.')
    // Reload data with the new language
    loadPageData()
  }

  // Handle login form submission
  async function handleLogin() {
    if (!email) {
      errorMessage = data.emailEmptyError || defaultData.emailEmptyError
      return
    }
    if (!password) {
      errorMessage = data.passwordEmptyError || defaultData.passwordEmptyError
      return
    }

    try {
      isLoading = true
      errorMessage = ''

      const authResponse = await fetch(
        'https://api.rastros.cl/api/auth/local',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ identifier: email, password: password }),
        }
      )

      if (!authResponse.ok) {
        const errorData = await authResponse.json()
        errorMessage = errorData.message || 'Credenciales inválidas.'
        return
      }

      const { jwt, user: authUserData } = await authResponse.json()

      // Store JWT and user data
      localStorage.setItem('jwt', jwt)
      localStorage.setItem('user', JSON.stringify(authUserData))

      // Call callUser to fetch and set the user store data
      await callUser(jwt)

      // Redirect to dashboard
      navigate('/')
    } catch (error) {
      console.error('Login error:', error)
      errorMessage = 'Ocurrió un error durante el inicio de sesión.'
    } finally {
      isLoading = false
    }
  }

  // Initialize on component mount
  onMount(() => {
    loadPageData()
  })
</script>

<div class="login-page-wrapper">
  <header class="login-header">
    <div class="header-logo">
      {#if isLoading}
        <div class="skeleton skeleton-logo"></div>
      {:else if data.logo?.url}
        <img src={data.logo.url} alt="Logo" class="img-fluid" />
      {:else}
        <img
          src="/src/assets/images/logo-enteleqqia-blanco.png"
          alt="Logo"
          class="img-fluid"
        />
      {/if}
    </div>
    <div class="header-right-content">
      <div class="contact-info">
        {#if isLoading}
          <div class="skeleton skeleton-text short"></div>
          <div class="skeleton skeleton-text short"></div>
        {:else}
          <span
            ><i class="mdi mdi-map-marker"></i> Badajoz 100 - Las Condes, Santiago,
            Chile</span
          >
          <span
            ><i class="mdi mdi-phone"></i> Tel: [+56 2] 3806640 Fax:[+56 2] 3806641</span
          >
        {/if}
      </div>
      <div class="language-selector">
        {#if isLoading}
          <div class="skeleton skeleton-dropdown"></div>
        {:else}
          <LanguageDropdown
            on:chageLang={handleLanguageChange}
            {availableLanguages}
            selectedLang={localStorage.getItem('LANGUAGE') || DEFAULT_LOCALE}
          />
        {/if}
      </div>
    </div>
  </header>
  <div class="login-container">
    <div class="left-panel">
      <!-- Background image will be set via CSS -->
    </div>
    <div class="right-panel">
      <main class="login-main">
        <div class="login-card">
          <h2 class="login-title">
            {#if isLoading}
              <div class="skeleton skeleton-text medium"></div>
            {:else}
              {data.title}
            {/if}
          </h2>

          {#if errorMessage}
            <div class="alert alert-danger" transition:fade>
              {errorMessage}
            </div>
          {/if}

          <form on:submit|preventDefault={handleLogin}>
            <div class="form-group">
              <Label for="email">
                {#if isLoading}
                  <div class="skeleton skeleton-text short"></div>
                {:else}
                  {data.userLabel}
                {/if}
              </Label>
              {#if isLoading}
                <div class="skeleton skeleton-input"></div>
              {:else}
                <Input
                  type="email"
                  id="email"
                  bind:value={email}
                  placeholder={data.emailPlaceholder}
                  disabled={isLoading}
                  class="form-control"
                />
              {/if}
            </div>

            <div class="form-group">
              <Label for="password">
                {#if isLoading}
                  <div class="skeleton skeleton-text short"></div>
                {:else}
                  {data.passwordLabel}
                {/if}
              </Label>
              {#if isLoading}
                <div class="skeleton skeleton-input"></div>
              {:else}
                <div class="input-group">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    bind:value={password}
                    placeholder={data.passwordPlaceholder}
                    disabled={isLoading}
                    class="form-control"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary toggle-password"
                    on:click={() => (showPassword = !showPassword)}
                    disabled={isLoading}
                  >
                    <i class={`mdi mdi-eye${showPassword ? '-off' : ''}`}></i>
                  </button>
                </div>
              {/if}
            </div>

            <div class="form-check mb-3">
              <input
                class="form-check-input"
                type="checkbox"
                id="remember-me"
                bind:checked={rememberMe}
              />
              <label class="form-check-label" for="remember-me">
                {#if isLoading}
                  <div class="skeleton skeleton-text short"></div>
                {:else}
                  {data.rememberLabel}
                {/if}
              </label>
            </div>

            {#if isLoading}
              <div class="skeleton skeleton-button"></div>
            {:else}
              <button
                type="submit"
                class="btn btn-primary w-100"
                disabled={isLoading || !email || !password}
              >
                {#if isLoading}
                  <span
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                  ></span>
                  Cargando...
                {:else}
                  {data.singInButtonLabel}
                {/if}
              </button>
            {/if}

            <div class="text-center mt-3">
              <a href="/auth-recoverpw" class="text-muted">
                {#if isLoading}
                  <div class="skeleton skeleton-text short"></div>
                {:else}
                  {data.forgetPassLabel}
                {/if}
              </a>
            </div>
          </form>
        </div>
      </main>

      <footer class="login-footer">
        {#if isLoading}
          <div class="skeleton skeleton-text medium"></div>
        {:else}
          <p>&copy; {currentYear} {data.copyright}</p>
        {/if}
      </footer>
    </div>
  </div>
</div>

<style>
  .login-page-wrapper {
    display: flex;
    flex-direction: column; /* Stack header and main content vertically */
    min-height: 100vh;
    font-family: 'Arial', sans-serif;
  }

  .login-container {
    /* This now holds left and right panels */
    display: flex;
    flex: 1; /* Take remaining vertical space */
  }

  .left-panel {
    flex: 1;
    background-image: url('https://rastros.cl/assets/images/roadway.jpg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    position: relative;
    display: none; /* Hidden by default on small screens */
  }

  .right-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 20px;
    background-color: #f8f9fa; /* Light background for the form side */
  }

  .login-header {
    display: flex;
    justify-content: space-between; /* Logo left, contact/lang right */
    align-items: center;
    background-color: #333333; /* Dark header background */
    color: #ffffff; /* White text for dark header */
    padding: 15px 20px; /* Add some padding */

    flex-wrap: wrap; /* Allow wrapping on smaller screens */
  }

  .header-logo img {
    max-height: 100px; /* Adjust logo size in header */
    width: auto;
  }

  .header-right-content {
    display: flex;
    flex-direction: column; /* Stack contact info and language selector */
    align-items: flex-end; /* Align to the right */
  }

  .contact-info {
    text-align: right; /* Align text to the right */
  }

  .contact-info span {
    display: block;
    font-size: 0.85em;
    color: inherit; /* Inherit color from header */
    margin-bottom: 5px;
  }

  .contact-info i {
    margin-right: 5px;
    color: inherit; /* Inherit color from header */
  }

  .language-selector {
    margin-top: 5px; /* Smaller space from contact info */
  }

  /* Remove original auth-logo styles */
  .auth-logo {
    display: none; /* Hide the original logo container */
  }

  .login-main {
    flex-grow: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px 0;
  }

  .login-card {
    background: #fff;
    padding: 40px;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 400px; /* Max width for the login form */
  }

  .auth-logo img {
    max-height: 80px; /* Adjust logo size */
    margin-bottom: 20px;
  }

  .login-title {
    text-align: center;
    color: #343a40;
    margin-bottom: 30px;
    font-size: 1.8em;
  }

  .form-group {
    margin-bottom: 20px;
  }

  .form-control {
    border-radius: 5px;
    padding: 10px 15px;
    border: 1px solid #ced4da;
  }

  .form-control:focus {
    border-color: #405189;
    box-shadow: 0 0 0 0.2rem rgba(64, 81, 137, 0.25);
  }

  .input-group .form-control {
    border-right: none;
  }

  .input-group .btn {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-color: #ced4da;
  }

  .btn-primary {
    background-color: #405189;
    border-color: #405189;
    padding: 10px 20px;
    font-size: 1.1em;
    border-radius: 5px;
  }

  .btn-primary:hover {
    background-color: #364574;
    border-color: #364574;
  }

  .alert {
    margin-bottom: 20px;
    font-size: 0.9em;
    text-align: center;
  }

  .login-footer {
    text-align: center;
    padding: 20px;
    background-color: #f8f9fa;
    border-top: 1px solid #e9ecef;
    color: #6c757d;
  }

  /* Skeleton styles */
  .skeleton {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 4px;
  }

  .skeleton-text {
    height: 1em;
    margin-bottom: 0.5em;
  }

  .skeleton-text.short {
    width: 50%;
  }

  .skeleton-text.medium {
    width: 75%;
  }

  .skeleton-text.long {
    width: 100%;
  }

  .skeleton-button {
    height: 40px;
    width: 100%;
  }

  .skeleton-input {
    height: 38px;
    width: 100%;
  }

  .skeleton-logo {
    height: 100px;
    width: 150px;
  }

  .skeleton-dropdown {
    height: 38px;
    width: 80px;
  }

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  /* Responsive adjustments */
  @media (min-width: 768px) {
    .left-panel {
      display: block; /* Show left panel on medium and larger screens */
    }

    .right-panel {
      flex: 0 0 50%; /* Right panel takes 50% width on larger screens */
      max-width: 50%;
    }

    .login-header {
      flex-wrap: nowrap; /* Prevent wrapping on larger screens */
    }

    .contact-info span {
      display: inline-block;
      margin-right: 15px;
      margin-bottom: 0;
    }

    .language-selector {
      margin-top: 0;
    }
  }

  @media (min-width: 992px) {
    .right-panel {
      flex: 0 0 40%; /* Adjust width for larger desktops */
      max-width: 40%;
    }
  }

  @media (min-width: 1200px) {
    .right-panel {
      flex: 0 0 35%; /* Further adjust for extra large screens */
      max-width: 35%;
    }
  }
</style>
